import { flatten, path, pick, pickAll, pipe, prop } from 'ramda'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg } from '@/hooks'
import useValidateEndpoint from '@/hooks/useValidate'
import { ConfigDataScreen } from '@/screens/ComponentLibrary/components'
import { useGetCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { parseComponentTypes } from '@/utils'
import { ComponentType } from '@/utils/api'
import splitAndTrim from '@/utils/splitAndTrim'

import { BubbleOptions, SemanticSearchSettings } from '../components'

const toArr = splitAndTrim('\n')

const toWorkshopArr = (bubble: BubbleOptions | string) => {
  if (typeof bubble === 'string')
    return bubble.split('---').map((s) => splitAndTrim('\n')(s))

  return bubble
    ?.map((b) => b.value || b)
    .map((str) => {
      // @ts-ignore
      if (str.nestedArray)
        //@ts-ignore
        return toArr(str.nestedArray.map(prop('value')).join('\n'))
      if (Array.isArray(str)) return toArr(str.join('\n'))
      // @ts-ignore
      return toArr(str)
    })
}

export const parseSemanticSearchData = (
  orgSlug?: string,
  param?: { settings: SemanticSearchSettings } | string | null
) => {
  const data = parseComponentTypes(param || '', ComponentType.semanticSearch)

  const getQuestionSet = (historyData: SemanticSearchSettings) => {
    const splitSources = splitAndTrim(',')
    const showSources =
      historyData.largeLanguageModelSettings.showSources ?? true
    return (
      historyData.questions?.reduce((acc, curr) => {
        const source = splitSources(curr.source) || ['vail']
        const relatedEgs = (curr.relatedEgs || []).map(
          pick(['egName', 'buttonLabel'])
        )
        return {
          ...acc,
          [curr.key]: {
            answer: [
              {
                type: 'text',
                value:
                  curr.answerText || 'missing answer, please add it in Vail',
              },
            ],
            question: curr.question,
            relatedEgs: relatedEgs.length >= 1 ? relatedEgs : null,
            sources: !!showSources ? source : null,
            suggestedQuestions: curr.suggestedQuestions || [],
          },
        }
      }, {}) ?? {}
    )
  }
  const getSemanticSearchConfig = (historyData: SemanticSearchSettings) => {
    const getSettings = pipe(
      path(['largeLanguageModelSettings']),
      pickAll([
        'adverseEventCheck',
        'adverseEventDetectedStep',
        'defaultQuestionKeys',
        'defaultQuestionText',
        'haveGptChooseBestQuestion',
        'initialSearchText',
        'keywordQuestionNotFoundText',
        'keywordSimilarQuestionsFoundText',
        'keywordSimilarQuestionsQuantity',
        'openAiAdverseEventTurboPrompt',
        'openAiCompletionPrompt',
        'pickFromAnswerEmbeddings',
        'pineconeNamespace',
        'pineconeTopK',
        'questionNotFoundButtons',
        'questionNotFoundText',
        'returningSearchText',
        'relatedQuestionsQuantity',
        'searchInstructions',
        'semanticSearchOutroStep',
        'showSources',
        'similarQuestionsFoundText',
        'similarQuestionsFoundButtons',
        'similarQuestionsQuantity',
        'submitQuestionButtonText',
        'submitQuestionCustomerEmail',
        'submitQuestionEnabled',
        'submitQuestionExtraBody',
        'submitQuestionText',
        'thresholdToGoStraightToAnswer',
        'thresholdToAddQuestionToSuggestions',
      ])
    )
    const settings = getSettings(
      historyData
    ) as SemanticSearchSettings['largeLanguageModelSettings']
    const defaultQuestionKeys =
      Array.isArray(settings.defaultQuestionKeys) &&
      settings.defaultQuestionKeys.filter(Boolean).length === 0
        ? ['']
        : settings.defaultQuestionKeys.filter(Boolean)

    return {
      ...settings,
      defaultQuestionKeys,
      defaultQuestionText: toArr(settings.defaultQuestionText),
      initialSearchText: toArr(settings.initialSearchText),
      keywordQuestionNotFoundText: toWorkshopArr(
        settings.keywordQuestionNotFoundText
      ),
      keywordSimilarQuestionsFoundText: toWorkshopArr(
        settings.keywordSimilarQuestionsFoundText
      ),
      openAiCompletionPrompt: null,
      questionNotFoundButtons:
        settings.questionNotFoundButtons?.map(pick(['action', 'label'])) ?? [],
      questionNotFoundText: toWorkshopArr(settings.questionNotFoundText),
      relatedEgsIntroText: null,
      returningSearchText: flatten(toWorkshopArr(settings.returningSearchText)),
      searchInstructions: toWorkshopArr(settings.searchInstructions),
      similarQuestionsFoundButtons:
        settings.similarQuestionsFoundButtons?.map(pick(['action', 'label'])) ??
        [],
      similarQuestionsFoundText: toWorkshopArr(
        settings.similarQuestionsFoundText
      ),
      submitQuestionText: toArr(settings.submitQuestionText),

      ...(settings.adverseEventCheck
        ? {
            adverseEventDetectedStep: settings.adverseEventDetectedStep,
          }
        : {
            adverseEventDetectedStep: null,
          }),
    }
  }
  if (!orgSlug || !data) return {}
  const semanticSearchConfig = getSemanticSearchConfig(data.settings)

  return {
    data: {
      questionSet: getQuestionSet(data.settings),
      semanticSearchConfig,
    },
    organization: orgSlug,
    schemaName: 'SemanticSearchConfig',
    schemaVersion: '2.0.0',
    version: '1.0.0',
  }
}

export const useParseSemanticSearchData = (
  orgSlug?: string,
  param?: { settings: SemanticSearchSettings } | string | null
) => {
  return useMemo(() => {
    return parseSemanticSearchData(orgSlug, param)
  }, [orgSlug, param])
}

export const ConfigData = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const org = useOrg(orgID)
  const current = useGetCustomComponent<SemanticSearchSettings>(componentID)
  const config = useParseSemanticSearchData(org.data?.slug, current?.data)
  const valid = useValidateEndpoint(config, componentID)

  return (
    <ConfigDataScreen
      config={config}
      current={current}
      org={org}
      rootName="Semantic Search"
      valid={valid}
    />
  )
}

if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  it.each([
    { expected: [], input: [] },
    {
      expected: [
        ['How else can I help?'],
        ['What would you like to search now?'],
        ['What else would you like to know?'],
        ['What would you like to learn about now?'],
        ['Can I answer another question for you?'],
        ['Do you have another question you want to ask?'],
      ],
      input: [
        ['How else can I help?'],
        ['What would you like to search now?'],
        ['What else would you like to know?'],
        ['What would you like to learn about now?'],
        ['Can I answer another question for you?'],
        ['Do you have another question you want to ask?'],
      ],
    },
    {
      expected: [['this is a string', 'that will NOT break']],
      input: 'this is a string \n that will NOT break',
    },
  ])('toWorkshopArr($input)', ({ input, expected }) => {
    // @ts-ignore
    expect(toWorkshopArr(input)).toEqual(expected)
  })
}
